import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@heroui/react";
import { Link, useLocation } from "react-router-dom";
import { FaAngleDown } from "react-icons/fa6";
import navItems from "./SideNavData";
// import { FaChevronRight } from "react-icons/fa";

const NavSubItem = ({
  item,
  hamClick,
  expandedSubCards,
  handleSubCardClick,
  isPathActive,
}) => {
  const hasSubItems = item.subcategory && item.subcategory.length > 0;

  if (hasSubItems) {
    const isSubOpen = !!expandedSubCards[item.id];
    const checkNestedActive = (el) => {
      if (el.path && isPathActive(el.path, el.activePaths, el.exact)) {
        return true;
      }
      if (el.subcategory && el.subcategory.length > 0) {
        return el.subcategory.some(checkNestedActive);
      }
      return false;
    };
    const isAnyNestedActive = item.subcategory.some(checkNestedActive);

    return (
      <div key={item.id} className="w-full">
        <Card
          isPressable
          onClick={() => handleSubCardClick(item.id)}
          className={`!shadow-none !outline-none w-full mt-[1px] !border-none hover:bg-[#2344d630] rounded-lg cursor-pointer flex items-center gap-3 transition duration-200 ease-in-out relative ${
            hamClick ? "justify-center pr-1.5" : ""
          } ${isAnyNestedActive ? "bg-[#2344d630]" : ""}`}
          title={hamClick ? item.label : ""}
        >
          <div
            className={`w-full flex items-center ${
              hamClick ? "p-1.5 justify-center" : "p-1.5 px-3 justify-start"
            }`}
          >
            <div
              className={`flex items-center gap-3 w-full text-sm !font-normal font-roboto text-gray-800 ${
                hamClick ? "justify-center" : "justify-start"
              } ${isAnyNestedActive ? "text-[#293B94] font-medium" : ""}`}
            >
              {isAnyNestedActive ? item.activeIcon : item.icon}
              {!hamClick && <span>{item.label}</span>}
              {!hamClick && (
                <FaAngleDown
                  className={`right-3 duration-200 absolute text-gray-500 transition-transform ${
                    isSubOpen ? "rotate-180" : ""
                  }`}
                />
              )}
            </div>
          </div>
        </Card>

        {/* Sub-accordion nested children */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isSubOpen && !hamClick
              ? "max-h-[600px] opacity-100"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="pl-2.5">
            {item.subcategory.map((nested) => (
              <NavSubItem
                key={nested.id || nested.path}
                item={nested}
                hamClick={hamClick}
                expandedSubCards={expandedSubCards}
                handleSubCardClick={handleSubCardClick}
                isPathActive={isPathActive}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const isItemActive = isPathActive(item.path, item.activePaths, item.exact);

  return (
    <Card
      key={item.path}
      isPressable
      className={`!shadow-none !outline-none w-full mt-[1px] !border-none hover:bg-[#2344d630] rounded-lg cursor-pointer flex items-center gap-3 transition duration-200 ease-in-out ${
        hamClick ? "justify-center pr-1.5" : ""
      } ${isItemActive ? "bg-[#2344d650]" : ""}`}
      title={hamClick ? item.label : ""}
    >
      <Link
        to={item.path}
        className={`w-full flex items-center ${
          hamClick ? "p-1.5 justify-center" : "p-1.5 px-3 justify-start"
        }`}
      >
        <div
          className={`flex items-center gap-3 w-full text-sm !font-normal font-roboto text-gray-800 ${
            hamClick ? "justify-center" : "justify-start"
          } ${isItemActive ? "text-[#293B94]" : ""}`}
        >
          {isItemActive ? item.activeIcon : item.icon}
          {!hamClick && <span>{item.label}</span>}
        </div>
      </Link>
    </Card>
  );
};

const SideNav = ({ hamClick, setHamClick }) => {
  const location = useLocation();
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedSubCards, setExpandedSubCards] = useState({});

  const isPathActive = useCallback(
    (targetPath, activePaths, exact = false) => {
      if (location.pathname.includes("setting")) return false;
      if (activePaths && Array.isArray(activePaths)) {
        if (
          activePaths.some((p) =>
            exact
              ? location.pathname === p
              : location.pathname === p ||
                location.pathname.startsWith(`${p}/`),
          )
        ) {
          return true;
        }
      }
      if (!targetPath) return false;
      if (exact) {
        return location.pathname === targetPath;
      }
      return (
        location.pathname === targetPath ||
        location.pathname.startsWith(`${targetPath}/`)
      );
    },
    [location.pathname],
  );

  const isCardActive = (item) => {
    if (location.pathname.includes("setting")) return false;
    const checkActive = (el) => {
      if (el.path && isPathActive(el.path, el.activePaths, el.exact)) {
        return true;
      }
      if (el.subcategory && el.subcategory.length > 0) {
        return el.subcategory.some(checkActive);
      }
      return false;
    };
    return checkActive(item);
  };

  // Set the expandedCard and expandedSubCards based on location.pathname
  useEffect(() => {
    navItems.forEach((section) => {
      section.items.forEach((item) => {
        if (item.subcategory) {
          const subExpansions = {};
          const checkAndExpand = (parent) => {
            if (!parent.subcategory) return false;
            let foundActive = false;
            parent.subcategory.forEach((sub) => {
              const isSelfActive =
                sub.path && isPathActive(sub.path, sub.activePaths, sub.exact);
              const isChildActive = checkAndExpand(sub);
              if (isSelfActive || isChildActive) {
                foundActive = true;
                if (sub.subcategory && sub.subcategory.length > 0) {
                  subExpansions[sub.id] = true;
                }
              }
            });
            return foundActive;
          };

          const isItemActive = checkAndExpand(item);
          if (isItemActive) {
            setExpandedCard(item.id);
            setExpandedSubCards((prev) => ({ ...prev, ...subExpansions }));
          }
        }
      });
    });
  }, [location.pathname, isPathActive]);

  const handleCardClick = (cardId) => {
    setHamClick(false);
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  const handleSubCardClick = (subId) => {
    setHamClick(false);
    setExpandedSubCards((prev) => ({
      ...prev,
      [subId]: !prev[subId],
    }));
  };

  return (
    <div
      className={`fixed top-0 bottom-0 left-0 h-screen max-h-screen z-[49] ${
        hamClick ? "w-[70px]" : "w-[250px]"
      } shadow-md rounded-none bg-white transition-[width] duration-200 flex flex-col justify-between overflow-hidden`}
    >
      {/* 1. Header / Logo - Clean, centered, never clipped */}
      <div
        className={`w-full ${
          hamClick ? "h-[55px] px-2" : "h-[65px] px-3"
        } flex items-center justify-center shrink-0 border-b border-gray-100 bg-white`}
      >
        <img
          src={hamClick ? "/small_logo.jpg" : "/big_logo.jpg"}
          alt="Nepal Life Logo"
          className={`${
            hamClick ? "h-9 w-9" : "h-11 w-auto"
          } max-w-full object-contain`}
        />
      </div>

      {/* 2. Scrollable Navigation Area */}
      <div
        className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-1 p-2 px-3 pb-6 text-black/80 sidenav-scrollbar ${
          hamClick ? "items-center px-1" : ""
        }`}
      >
        {navItems.map((section) => {
          const isFinancialOverview = section.id === 1;
          return (
            <div
              key={section.id}
              className={`flex flex-col gap-1 ${
                isFinancialOverview && !hamClick
                  ? "sticky top-0 z-20 bg-white pt-1 pb-2 border-b border-gray-100"
                  : isFinancialOverview && hamClick
                    ? "sticky top-0 z-20 bg-white pt-1 pb-2 border-b border-gray-100"
                    : "pt-2"
              }`}
            >
              {!hamClick && (
                <p className="text-[0.75rem] font-bold font-roboto text-[#293B94] uppercase tracking-wider mb-1 px-1">
                  {section.category}
                </p>
              )}
              {section.items.map((item) => (
                <React.Fragment key={item.id}>
                  {!item.subcategory ? (
                    <Card
                      isPressable
                      className={`!shadow-none !outline-none w-full !border-none hover:bg-[#2344d630] rounded-lg cursor-pointer flex items-center gap-3 transition duration-200 ease-in-out ${
                        hamClick ? "justify-center" : ""
                      } ${isPathActive(item.path, item.activePaths, item.exact) ? "bg-[#2344d650]" : ""}`}
                      title={hamClick ? item.label : ""}
                    >
                      <Link
                        to={item.path}
                        className={`w-full flex items-center ${
                          hamClick
                            ? "p-1.5 justify-center"
                            : "p-1.5 px-3 justify-start"
                        }`}
                      >
                        <div
                          className={`flex items-center gap-3 w-full text-sm !font-normal font-roboto text-gray-800 ${
                            hamClick ? "justify-center" : "justify-start"
                          } ${isPathActive(item.path, item.activePaths, item.exact) ? "text-[#293B94]" : ""}`}
                        >
                          {isPathActive(item.path, item.activePaths, item.exact)
                            ? item.activeIcon
                            : item.icon}
                          {!hamClick && <span>{item.label}</span>}
                        </div>
                      </Link>
                    </Card>
                  ) : (
                    <div key={item.id}>
                      <Card
                        isPressable
                        className={`!shadow-none !outline-none w-full !border-none hover:bg-[#2344d630] rounded-lg cursor-pointer flex items-center gap-3 transition duration-200 ease-in-out ${
                          hamClick ? "justify-center" : ""
                        } ${isCardActive(item) ? "bg-[#2344d650]" : ""}`}
                        onClick={() => handleCardClick(item.id)}
                        title={hamClick ? item.label : ""}
                      >
                        <div
                          className={`w-full flex items-center ${
                            hamClick
                              ? "p-1.5 justify-center"
                              : "p-1.5 px-3 justify-start"
                          }`}
                        >
                          <div
                            className={`flex items-center gap-3 w-full text-sm !font-normal font-roboto text-gray-800 ${
                              hamClick ? "justify-center" : "justify-start"
                            }`}
                          >
                            {isCardActive(item) ? item.activeIcon : item.icon}
                            {!hamClick && <span>{item.label}</span>}
                            {!hamClick && (
                              <FaAngleDown
                                className={`right-3 duration-200 absolute ${
                                  expandedCard === item.id ? "rotate-180" : ""
                                }`}
                              />
                            )}
                          </div>
                        </div>
                      </Card>
                      <div
                        className={`transition-max-height duration-300 ease-in-out overflow-hidden ${
                          expandedCard === item.id ? "max-h-[800px]" : "max-h-0"
                        }`}
                      >
                        <div className="pl-2">
                          {item.subcategory.map((e) => (
                            <NavSubItem
                              key={e.id || e.path}
                              item={e}
                              hamClick={hamClick}
                              expandedSubCards={expandedSubCards}
                              handleSubCardClick={handleSubCardClick}
                              isPathActive={isPathActive}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          );
        })}
      </div>
      <div
        className={`shrink-0 py-2.5 px-4 text-left border-t border-gray-100 bg-white text-gray-500 font-roboto select-none ${
          hamClick ? "text-[10px] truncate text-center" : "text-xs"
        }`}
      >
        {!hamClick ? "Developed by Qpixel" : "Qpixel"}
      </div>
    </div>
  );
};

export default SideNav;
